import type { RegisterFunctions } from "@repo/database";
import { useEffect } from "react";
import { ButtonSchema } from "@repo/validators";
import { useTranslation } from "react-i18next";

import type { PartialButton } from "~/providers/button-provider";
import { useButtonManager } from "~/managers/use-button-manager";
import { useFunctionManager } from "~/managers/use-function-manager";
import { useUnsavedChanges } from "~/providers/unsaved-changes-provider";
import { ColorPicker } from "../ui/color-picker";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useForm,
} from "../ui/form";
import { Input } from "../ui/input";
import { MultiSelect } from "../ui/multi-select";

export default function ButtonForm({
  button,
  onClose,
}: {
  button: PartialButton;
  onClose: (force?: boolean) => void;
}) {
  const { t } = useTranslation("common");
  const { getLabel, getAvailableFunctions, getDefaultColor } =
    useFunctionManager();

  const { setUnsavedChanges } = useUnsavedChanges();

  const { updateButton, createButton } = useButtonManager();

  const form = useForm({
    schema: ButtonSchema,
    defaultValues: {
      position: button.position,
      location: button.location,
      function: button.function,
      mobile: button.mobile,
      label: button.label ?? "",
      color: button.color ?? "",
    },
  });

  const isDirty = !!Object.values(form.formState.dirtyFields).length;

  const functionOptions = getAvailableFunctions(button.location).map(
    (func) => ({
      label: getLabel(func),
      value: func,
    }),
  );

  const handleSubmit = form.handleSubmit(async (formData) => {
    if (button.id) {
      const result = await updateButton({
        id: button.id,
        ...formData,
      });
      if (result) {
        form.reset();
        onClose(true);
      }
    } else {
      const result = await createButton(formData);
      if (result) {
        form.reset();
        onClose(true);
      }
    }
  });

  useEffect(() => {
    setUnsavedChanges({
      isDirty,
      onDiscard() {
        form.reset();
        onClose(true);
      },
    });
  }, [form, isDirty, onClose, setUnsavedChanges]);

  return (
    <Form {...form}>
      <form
        onSubmit={handleSubmit}
        id="button-form"
        className="grid grid-cols-2 gap-2"
      >
        <FormField
          control={form.control}
          name="function"
          render={({ field }) => (
            <FormItem className="col-span-full">
              <FormLabel>{t("Button.function")}</FormLabel>
              <FormControl>
                <MultiSelect
                  placeholder={t("Button.function_placeholder")}
                  onValueChange={(value) => {
                    field.onChange(value[0]);
                    form.setValue(
                      "color",
                      getDefaultColor(value[0] as RegisterFunctions),
                    );
                  }}
                  maxSelect={1}
                  options={functionOptions.sort((a, b) =>
                    a.label.localeCompare(b.label),
                  )}
                  defaultValue={field.value ? [field.value] : []}
                  value={[field.value]}
                />
              </FormControl>

              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="label"
          render={({ field }) => (
            <FormItem className="col-span-full">
              <FormLabel>{t("Button.label")}</FormLabel>
              <Input {...field} placeholder="Label" />
              <FormDescription>{t("Button.label_description")}</FormDescription>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="color"
          render={({ field }) => (
            <FormItem className="col-span-full">
              <FormLabel>{t("Button.color")}</FormLabel>
              <ColorPicker {...field} />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="position"
          render={({ field }) => (
            <FormItem className="col-span-full">
              <FormLabel>{t("Button.position")}</FormLabel>
              <MultiSelect
                maxSelect={1}
                placeholder={t("Button.position_placeholder")}
                onValueChange={(value) => {
                  field.onChange(parseInt(value[0]));
                }}
                options={Array.from({ length: 18 }).map((_, idx) => ({
                  label: (idx + 1).toString(),
                  value: (idx + 1).toString(),
                }))}
                defaultValue={field.value ? [field.value.toString()] : []}
                value={field.value}
              />
            </FormItem>
          )}
        />
      </form>
    </Form>
  );
}
